import * as React from "react";
import { RiskTolerancePayload, RiskToleranceWizard } from "@earned/wizard";
import useTasksStore from "@modules/tasks/state";
import { useRiskToleranceInfo } from "@modules/tasks/queries";
import { useUpdateRiskToleranceInfo } from "@modules/tasks/mutations";
import { TaskTemplateId, WizardId } from "@modules/tasks/types";
import { useWizardCloseConfirm } from "@modules/tasks/hooks.web";
import { NoCacheWizardExitMessage } from "@modules/tasks/constants";

interface RiskToleranceWizardControllerProps {
  clients: Forme.ClientUser[];
}

const RiskToleranceWizardController: React.FC<
  RiskToleranceWizardControllerProps
> = ({ clients }) => {
  const {
    wizardId,
    setWizardId,
    isWizardOpen,
    setIsWizardOpen,
    wizardTask,
    setWizardTask,
    setRemoteCompleteTaskId,
  } = useTasksStore();
  const client = React.useMemo(() => {
    return clients.filter((c) =>
      c.isPrimary
        ? wizardTask?.templateId === TaskTemplateId.RISK_TOLERANCE_QUESTIONNAIRE
        : wizardTask?.templateId ===
          TaskTemplateId.RISK_TOLERANCE_QUESTIONNAIRE_COCLIENT
    )[0] as Forme.ClientUser;
  }, [clients, wizardTask]);
  const { riskToleranceInfo, isLoading } = useRiskToleranceInfo(client.id);
  const {
    mutateAsync,
    isLoading: isSubmitting,
    isSuccess,
    reset,
  } = useUpdateRiskToleranceInfo(client.id);

  const isOpen = React.useMemo(
    () => wizardId === WizardId.RISK_TOLERANCE && isWizardOpen,
    [wizardId, isWizardOpen]
  );

  const { openCloseConfirmDialog, CloseConfirmDialogComponent } =
    useWizardCloseConfirm({
      onConfirmClose: reset,
      dialogMessage: NoCacheWizardExitMessage,
    });

  const handleSubmit = React.useCallback(
    async (payload: RiskTolerancePayload) => {
      await mutateAsync({ data: payload });
    },
    [mutateAsync]
  );

  const handleDone = React.useCallback(async () => {
    if (wizardTask) setRemoteCompleteTaskId(wizardTask.id);
    setWizardTask(null);
    setWizardId(null);
    setIsWizardOpen(false);
    reset();
  }, [
    reset,
    setWizardId,
    setWizardTask,
    setRemoteCompleteTaskId,
    setIsWizardOpen,
    wizardTask,
  ]);
  return (
    <>
      <RiskToleranceWizard
        infoOwnerName={
          clients.length === 1
            ? undefined
            : `${client.firstName} ${client.lastName}`
        }
        isOpen={isOpen}
        close={openCloseConfirmDialog}
        onSubmit={handleSubmit}
        isSuccess={isSuccess}
        isSubmitting={isSubmitting}
        isLoading={isLoading && !riskToleranceInfo}
        onDone={handleDone}
        cache={riskToleranceInfo}
        modalRootSelector="#wizard-root"
      />
      <CloseConfirmDialogComponent />
    </>
  );
};

export default RiskToleranceWizardController;
